import React, { useCallback, useImperativeHandle, useState } from "react";
import type { Ref } from "react";
import type { Plaid } from "plaid-link";
import { isFunction } from "@common/helpers/typeGuards";
import { CreateBankAccountConnectionDto } from "@common/dto/onboarding/create-bank-account-connection.dto";
import { PlaidConnection } from "@common/types/plaidConnection";
import { CreatePlaidConnectionResponse } from "@common/dto/plaid/create-plaid-connection-response.dto";
import { FullAccountNumber } from "@common/dto/financialAccount/get-account-numbers-response.dto";
import { Entity } from "@common/types/entity";
import { PLAID_INSTITUTIONS_NAMES } from "@common/constants";
import { useBankConnectionCreationMutation } from "@mutations/plaidConnection";
import { useConnectTarget } from "@hooks/useConnectTarget";
import { connectWithPlaid, getPlaidConnections } from "@lib/plaidConnection";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useConnectAccount } from "../../connectAccount/ConnectAccount.context";
import {
    ConnectionActionButton,
    ConnectionButtonSize,
    ConnectionButtonVariant,
} from "@components/financialConnection/ConnectionActionButton";
import { CreateAccountHandle } from "../../accounts/AddAccounts/lib";
import { PossibleConnectionDuplicatesModal } from "./PossibleConnectionDuplicatesModal";

export interface ConnectPlaidAccountProps {
    onSavingConnection?: (saving: boolean) => void;
    btnVariant?: ConnectionButtonVariant;
    btnText?: React.ReactNode;
    btnSize?: ConnectionButtonSize;
    btnClass?: string;
    institutionName?: PLAID_INSTITUTIONS_NAMES;
    onConnected?: (
        connection: PlaidConnection,
        numbers: FullAccountNumber[],
    ) => void;
    disabled?: boolean;
    defaultEntity?: Entity;
    /**
     * Skips the post-connect "Personal or Business" classification modal.
     * Used by flows that assign accounts to entities elsewhere (e.g. the org
     * integrations table).
     */
    skipAccountTypeConfirmation?: boolean;
    beforeConnect?: () => Promise<void>;
    children?: (props: {
        connecting: boolean;
        initializeConnection: () => void;
    }) => React.ReactNode;
}

interface SavedLinkResponse {
    publicToken: string;
    linkSessionId?: string;
}

interface HandleSuccessfulConnectionParams {
    publicToken: string;
    metadata: Plaid.OnSuccessMetaData;
    linkSessionId?: string;
}

interface FinalizeConnectionParams {
    publicToken: string;
    linkSessionId?: string;
    mergeByMask?: boolean;
}

interface FinishSavedConnectionParams {
    mergeByMask: boolean;
}

export const ConnectPlaid = ({
    btnVariant = "primary",
    btnText = "Connect Now",
    btnSize = "md",
    btnClass,
    institutionName,
    onSavingConnection,
    onConnected,
    disabled,
    defaultEntity,
    skipAccountTypeConfirmation,
    beforeConnect,
    children,
    ref,
}: ConnectPlaidAccountProps & { ref?: Ref<CreateAccountHandle> }) => {
    const { updateConnectAccountState } = useConnectAccount();
    const { workspaceId, organizationId } = useConnectTarget();
    const [connecting, setConnecting] = useState(false);
    const [savedLinkResponse, setSavedLinkResponse] =
        useState<SavedLinkResponse>();
    const [possibleDuplicates, setPossibleDuplicates] =
        useState<PlaidConnection[]>();

    const createConnection = useBankConnectionCreationMutation({
        skipAccountTypeConfirmation,
    });

    const handleSuccessfulConnection = useCallback(
        async ({
            publicToken,
            metadata,
            linkSessionId,
        }: HandleSuccessfulConnectionParams) => {
            if (!metadata.institution) {
                return { publicToken, linkSessionId };
            }

            try {
                const similarConnections = await getPlaidConnections(
                    metadata.institution.institution_id,
                );

                if (similarConnections.length > 0) {
                    setPossibleDuplicates(similarConnections);
                    setSavedLinkResponse({
                        publicToken,
                        linkSessionId,
                    });
                    return;
                }
            } catch {
                return { publicToken, linkSessionId };
            }

            return { publicToken, linkSessionId };
        },
        [],
    );

    const authorize = useCallback(async () => {
        setConnecting(true);

        const connectResult = await connectWithPlaid({
            institutionName,
            workspaceId,
            organizationId,
        });

        if (connectResult.connected) {
            return await handleSuccessfulConnection({
                publicToken: connectResult.publicToken,
                metadata: connectResult.metadata,
                linkSessionId: connectResult.linkSessionId,
            });
        } else {
            setConnecting(false);
        }
    }, [
        handleSuccessfulConnection,
        institutionName,
        organizationId,
        workspaceId,
    ]);

    const connect = useCallback(
        async (data: CreateBankAccountConnectionDto) => {
            onSavingConnection?.(true);
            updateConnectAccountState({
                isConnecting: true,
            });
            const response = await createConnection.mutateAsync(data);
            onSavingConnection?.(false);

            return response;
        },
        [onSavingConnection, updateConnectAccountState, createConnection],
    );

    const onBankConnected = useCallback(
        ({ connection, numbers }: CreatePlaidConnectionResponse) => {
            setConnecting(false);
            onConnected?.(connection, numbers);
        },
        [onConnected],
    );

    const finalizeConnection = useCallback(
        async ({
            publicToken,
            linkSessionId,
            mergeByMask,
        }: FinalizeConnectionParams) => {
            const connectedResult = await connect({
                publicToken,
                defaultEntityId: defaultEntity?.id,
                linkSessionId,
                mergeByMask,
            });
            onBankConnected(connectedResult);
        },
        [onBankConnected, connect, defaultEntity],
    );

    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const initializeConnection = useCallback(async () => {
        if (connecting) {
            return;
        }

        // The link token is a GET, so the server has nothing to refuse, and
        // the first refusable request is the connection itself — after the
        // user has already signed in at their bank. Refuse before Plaid
        // opens.
        if (refuseIfViewOnly()) {
            return;
        }

        try {
            if (beforeConnect) {
                await beforeConnect();
            }

            const result = await authorize();

            if (result) {
                await finalizeConnection(result);
            }
        } catch {
            setConnecting(false);
        }
    }, [
        authorize,
        beforeConnect,
        connecting,
        finalizeConnection,
        refuseIfViewOnly,
    ]);

    const finishSavedConnection = useCallback(
        ({ mergeByMask }: FinishSavedConnectionParams) => {
            const saved = savedLinkResponse;
            setPossibleDuplicates(undefined);
            setSavedLinkResponse(undefined);

            if (!saved) {
                return;
            }

            finalizeConnection({
                publicToken: saved.publicToken,
                linkSessionId: saved.linkSessionId,
                mergeByMask,
            }).catch(() => setConnecting(false));
        },
        [finalizeConnection, savedLinkResponse],
    );

    const onSameLogin = useCallback(() => {
        finishSavedConnection({ mergeByMask: true });
    }, [finishSavedConnection]);

    const onNewLogin = useCallback(() => {
        finishSavedConnection({ mergeByMask: false });
    }, [finishSavedConnection]);

    useImperativeHandle(
        ref,
        () => ({
            initializeConnection: () => {
                if (possibleDuplicates || connecting) {
                    return;
                }

                return initializeConnection();
            },
        }),
        [connecting, initializeConnection, possibleDuplicates],
    );

    return (
        <>
            {isFunction(children) ? (
                children({ connecting, initializeConnection })
            ) : (
                <ConnectionActionButton
                    loading={connecting}
                    onClick={initializeConnection}
                    variant={btnVariant}
                    size={btnSize}
                    className={btnClass}
                    disabled={disabled}
                    data-testid="connect-plaid-btn"
                >
                    {btnText}
                </ConnectionActionButton>
            )}

            {possibleDuplicates && (
                <PossibleConnectionDuplicatesModal
                    show={!!possibleDuplicates}
                    connections={possibleDuplicates}
                    onSameLogin={onSameLogin}
                    onNewLogin={onNewLogin}
                />
            )}
        </>
    );
};
